import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Copy, Download, Loader2, RefreshCw, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Section({ title, children, testid }) {
  return (
    <div className="mb-6" data-testid={testid}>
      <h3 className="text-xs uppercase tracking-widest text-primary/90 mb-2" style={{ fontFamily: 'Manrope', fontWeight: 600 }}>
        {title}
      </h3>
      <div className="text-sm text-foreground/85 leading-relaxed">{children}</div>
    </div>
  );
}

function Bullets({ items }) {
  if (!items?.length) return <p className="text-muted-foreground italic">—</p>;
  return (
    <ul className="list-disc pl-5 space-y-1.5">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function PlanSkeleton() {
  const sections = [
    { h: "w-32", lines: 3 },
    { h: "w-44", lines: 4 },
    { h: "w-24", lines: 2 },
    { h: "w-52", lines: 5 },
    { h: "w-28", lines: 3 },
    { h: "w-56", lines: 4 },
  ];
  return (
    <div className="fade-in" data-testid="plan-skeleton">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Sparkles className="w-4 h-4 text-primary animate-pulse" strokeWidth={1.5} />
        <span>Drafting your plan… this usually takes 8–15 seconds.</span>
      </div>
      {sections.map((s, i) => (
        <div key={i} className="mb-6">
          <Skeleton className={`h-3 ${s.h} mb-3 bg-muted/70`} />
          <div className="space-y-2">
            {Array.from({ length: s.lines }).map((_, j) => (
              <Skeleton
                key={j}
                className={`h-3 ${j === s.lines - 1 ? 'w-3/5' : 'w-full'} bg-muted/50`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ConflictPlanDialog({ open, onOpenChange, sessionId }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sessions/${sessionId}/conflict-plan`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load plan");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && sessionId) load();
    // eslint-disable-next-line
  }, [open, sessionId]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/conflict-plan/regenerate`);
      setData(r.data);
      toast.success("Plan regenerated");
    } catch {
      toast.error("Could not regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const copyText = async () => {
    if (!data?.text) return;
    try {
      await navigator.clipboard.writeText(data.text);
      toast.success("Plan copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("crc_token");
      const res = await fetch(`${API}/sessions/${sessionId}/conflict-plan.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeTitle = (data?.title || "session").replace(/\s+/g, "_");
      a.download = `Conflict_Plan_${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const emailToMe = async () => {
    if (emailing) return;
    setEmailing(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/conflict-plan/email`, {});
      toast.success(`Sent to ${r.data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally {
      setEmailing(false);
    }
  };

  const plan = data?.plan;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-3xl max-w-3xl max-h-[88vh] flex flex-col p-0 overflow-hidden"
        data-testid="conflict-plan-dialog"
      >
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-border/60">
          <DialogTitle style={{ fontFamily: 'Manrope' }} className="text-2xl tracking-tight">
            Conflict Reflection & Collaboration Plan
          </DialogTitle>
          <DialogDescription>
            A calm, one-page plan you can print, copy, or carry into the conversation.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 px-8 py-6">
          {loading && <PlanSkeleton />}

          {!loading && plan && (
            <div className="fade-in" data-testid="plan-content">
              <Section title="Introduction" testid="plan-introduction">
                <p>{plan.introduction}</p>
              </Section>

              <Section title="Conflict summary" testid="plan-conflict-summary">
                <p className="whitespace-pre-wrap">{plan.conflict_summary}</p>
              </Section>

              <Section title="Positions" testid="plan-positions">
                <p className="mb-2"><span className="font-medium">Your position:</span> {plan.your_position}</p>
                <p><span className="font-medium">Other person's position:</span> {plan.their_position}</p>
              </Section>

              <Section title="Deeper needs & interests" testid="plan-needs">
                <p className="font-medium mb-1">Your needs</p>
                <Bullets items={plan.your_needs} />
                <p className="font-medium mt-3 mb-1">Other person's likely needs <span className="text-xs font-normal text-muted-foreground">(inferred)</span></p>
                <Bullets items={plan.their_needs} />
              </Section>

              <Section title="Key insight" testid="plan-insight">
                <p>{plan.key_insight}</p>
              </Section>

              <Section title="Possible collaborative strategies" testid="plan-strategies">
                <Bullets items={plan.strategies} />
              </Section>

              <Section title="Suggested conversation opening" testid="plan-opening">
                <p className="whitespace-pre-wrap bg-muted/60 border-l-2 border-primary/40 pl-4 py-3 pr-3 rounded-r-xl italic">
                  {plan.conversation_opening}
                </p>
              </Section>

              <Section title="Suggested next step" testid="plan-next-step">
                <p>{plan.next_step}</p>
              </Section>

              <Section title="Reflection prompts" testid="plan-prompts">
                <Bullets items={plan.reflection_prompts} />
              </Section>

              <p className="text-xs italic text-muted-foreground border-t border-border/60 pt-4 mt-2" data-testid="plan-disclaimer">
                This document is for personal reflection and guidance. It is not professional, legal, or therapeutic advice.
              </p>
            </div>
          )}
        </ScrollArea>

        <div className="border-t border-border/60 px-6 py-4 flex flex-wrap items-center justify-between gap-2 bg-background">
          <Button
            variant="ghost"
            size="sm"
            onClick={regenerate}
            disabled={loading || regenerating || !plan}
            className="rounded-full text-muted-foreground"
            data-testid="plan-regenerate-button"
          >
            {regenerating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" strokeWidth={1.5} />}
            Regenerate
          </Button>
          <div className="flex gap-2 ml-auto flex-wrap">
            <Button
              variant="outline"
              onClick={emailToMe}
              disabled={loading || emailing || !plan || !user?.email}
              className="rounded-full"
              data-testid="plan-email-button"
              title={user?.email ? `Email to ${user.email}` : ""}
            >
              {emailing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" strokeWidth={1.5} />}
              Email to me
            </Button>
            <Button
              variant="outline"
              onClick={copyText}
              disabled={loading || !plan}
              className="rounded-full"
              data-testid="plan-copy-text-button"
            >
              <Copy className="w-4 h-4 mr-1" strokeWidth={1.5} /> Copy text
            </Button>
            <Button
              onClick={downloadPdf}
              disabled={loading || downloading || !plan}
              className="rounded-full"
              data-testid="plan-download-pdf-button"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" strokeWidth={1.5} />}
              Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
